import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import type { AuditEntry, Paginated } from '@worki/shared';
import { api, buildQuery } from '../lib/api.js';
import { useDebounced } from '../lib/useDebounced.js';
import { usePageSearch } from '../lib/pageSearch.js';
import { currentLocale } from '../i18n.js';
import { formatDateTime } from '../lib/format.js';
import { actorName } from '../lib/actorName.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Icon } from '../components/Icon.js';

/** "#12 · דנה כהן", or the entity type when the entry carries no details. */
function entityLabel(row: AuditEntry, t: TFunction): string {
  const details = row.details ?? {};
  const number = details.number;
  const name = details.customer ?? details.name ?? details.title;
  return (
    [number ? `#${String(number)}` : null, name ? String(name) : null].filter(Boolean).join(' · ') ||
    t(`enum.entityType.${row.entityType}`, { defaultValue: row.entityType })
  );
}

/**
 * DASH-009 — the full activity history.
 *
 * Read-only by construction: the audit log has no update or delete path in the
 * API, and the database refuses both, so this screen offers no row actions.
 */
export function ActivityPage() {
  const { t } = useTranslation();
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search, 300);
  /* UI-11 — the box the list drew is in the bar now, beside the screen name. */
  usePageSearch(search, setSearch, t('common.search'));

  useEffect(() => setPage(1), [debouncedSearch]);

  const list = useQuery({
    queryKey: ['activity', { debouncedSearch, page }],
    queryFn: () =>
      api.get<Paginated<AuditEntry>>(`/activity${buildQuery({ q: debouncedSearch, page, pageSize: 30 })}`),
  });

  const columns: Array<Column<AuditEntry>> = [
    {
      key: 'action',
      header: t('activity.action'),
      // `summary` is an i18n key emitted by the audit service.
      render: (row) => t(row.summary, { defaultValue: row.action }),
    },
    {
      key: 'entity',
      header: t('activity.entity'),
      render: (row) => entityLabel(row, t),
    },
    {
      key: 'actor',
      header: t('activity.actor'),
      render: (row) => actorName(row.actorLabel, t),
    },
    {
      key: 'when',
      header: t('activity.when'),
      render: (row) => <span className="ltr-nums">{formatDateTime(row.createdAt, locale)}</span>,
    },
  ];

  return (
    <>
      <div className="page-header">
        <p className="muted">{t('activity.subtitle')}</p>
      </div>

      <DataTable
        rows={list.data?.items ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        // Four columns of short strings read better as a line of history: what
        // happened, then who and when underneath it.
        card={(row) => (
          <>
            <div className="list-card__head">
              <span className="list-card__title">
                <Icon name="activity" size={17} />
                {t(row.summary, { defaultValue: row.action })}
              </span>
            </div>
            {entityLabel(row, t) && <p className="list-card__sub">{entityLabel(row, t)}</p>}
            <div className="list-card__meta">
              <span>{actorName(row.actorLabel, t)}</span>
              <span className="ltr-nums">{formatDateTime(row.createdAt, locale)}</span>
            </div>
          </>
        )}
        page={page}
        pageSize={list.data?.pageSize ?? 30}
        total={list.data?.total ?? 0}
        onPageChange={setPage}
        empty={{ icon: '🗂️', title: t('activity.empty') }}
      />
    </>
  );
}
